/* ==========================================================================
   Brewell theme JavaScript
   Custom elements only. Nothing is added to the global namespace except the
   Brewell object created in layout/theme.liquid.
   Every component is defensive: it checks for the nodes it needs, cleans up
   after itself, and survives being re-rendered by the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  var routes = (window.Brewell && window.Brewell.routes) || {};
  var strings = (window.Brewell && window.Brewell.strings) || {};

  /* ---------------------------------------------------------------- helpers */

  function announce(message) {
    var region = document.getElementById('a11y-status');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function formatMoney(cents) {
    var format = (window.Brewell && window.Brewell.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])';

  function focusableWithin(node) {
    return Array.prototype.filter.call(node.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function fetchSection(sectionId, url) {
    var target = url || window.location.pathname;
    var separator = target.indexOf('?') > -1 ? '&' : '?';
    return fetch(target + separator + 'section_id=' + sectionId)
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed');
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  function define(name, constructor) {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, constructor);
    }
  }

  /* ------------------------------------------------------------ cart events */

  var CART_UPDATED = 'brewell:cart:updated';

  function publishCartUpdate(cart, source) {
    // `source` lets the component that made the change skip its own listener,
    // so it refreshes once rather than twice.
    document.dispatchEvent(new CustomEvent(CART_UPDATED, { detail: { cart: cart, source: source || null } }));
  }

  function refreshCartCount() {
    return fetch(routes.cart + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (node) {
          node.textContent = cart.item_count;
          node.hidden = cart.item_count === 0;
        });
        var label = document.querySelector('[data-cart-count-announcement]');
        if (label && strings.itemsInCart) {
          label.textContent = strings.itemsInCart.replace('[count]', cart.item_count);
        }
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /* -------------------------------------------------------------- drawers */

  var DrawerManager = {
    open: null,
    lastFocus: null,

    show: function (drawer) {
      if (!drawer) return;
      this.lastFocus = document.activeElement;
      this.open = drawer;
      drawer.hidden = false;
      document.body.classList.add('overflow-hidden');

      document.querySelectorAll('[data-drawer-open="' + drawer.id + '"]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      });

      var focusables = focusableWithin(drawer);
      var first = drawer.querySelector('[data-drawer-close]') || focusables[0];
      if (first) first.focus();
    },

    hide: function () {
      var drawer = this.open;
      if (!drawer) return;
      drawer.hidden = true;
      document.body.classList.remove('overflow-hidden');

      document.querySelectorAll('[data-drawer-open="' + drawer.id + '"]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.open = null;
      if (this.lastFocus && document.body.contains(this.lastFocus)) {
        this.lastFocus.focus();
      }
      this.lastFocus = null;
    },

    trapFocus: function (event) {
      var drawer = this.open;
      if (!drawer || event.key !== 'Tab') return;

      var focusables = focusableWithin(drawer);
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      var id = opener.getAttribute('data-drawer-open');
      var drawer = document.getElementById(id);
      // Cart icon is a real link, so it still works with JS disabled.
      if (drawer) {
        event.preventDefault();
        DrawerManager.show(drawer);
      }
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      DrawerManager.hide();
      return;
    }

    // Clicking the scrim (the drawer element itself) closes it.
    if (DrawerManager.open && event.target === DrawerManager.open) {
      DrawerManager.hide();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && DrawerManager.open) {
      event.preventDefault();
      DrawerManager.hide();
      return;
    }
    DrawerManager.trapFocus(event);
  });

  /* -------------------------------------------------------- header search row */

  // A row inside the header, not an overlay: no scrim, no focus trap, and the
  // page keeps scrolling behind it.
  function searchRowToggle(expanded) {
    var row = document.querySelector('[data-search-row]');
    if (!row) return;

    row.hidden = !expanded;
    document.querySelectorAll('[data-search-toggle]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    if (expanded) {
      var input = row.querySelector('input[type="search"], input[name="q"]');
      if (input) input.focus();
    }
    publishAnchorOffset();
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-search-toggle]');
    if (toggle) {
      event.preventDefault();
      searchRowToggle(toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    var row = document.querySelector('[data-search-row]');
    if (!row || row.hidden) return;
    // Clicking anywhere outside the row closes it, the header included.
    if (!row.contains(event.target)) searchRowToggle(false);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var row = document.querySelector('[data-search-row]');
    if (!row || row.hidden) return;

    event.preventDefault();
    searchRowToggle(false);
    var toggle = document.querySelector('[data-search-toggle]');
    if (toggle) toggle.focus();
  });

  /* ------------------------------------------------------ details disclosure */

  define(
    'details-disclosure',
    class DetailsDisclosure extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('details');
        if (!this.details) return;

        this.onToggle = this.onToggle.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.details.addEventListener('toggle', this.onToggle);
        this.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        if (this.details) this.details.removeEventListener('toggle', this.onToggle);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
      }

      onToggle() {
        if (this.details.open) {
          document.addEventListener('click', this.onDocumentClick);
        } else {
          document.removeEventListener('click', this.onDocumentClick);
        }
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.details.open = false;
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.details.open) {
          this.details.open = false;
          var summary = this.details.querySelector('summary');
          if (summary) summary.focus();
        }
      }
    }
  );

  /* ------------------------------------------------------------- accordion */

  define(
    'accordion-group',
    class AccordionGroup extends HTMLElement {
      connectedCallback() {
        this.allowMultiple = this.dataset.allowMultiple === 'true';
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        var trigger = event.target.closest('.accordion__trigger');
        if (!trigger || !this.contains(trigger)) return;

        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;

        var isOpen = trigger.getAttribute('aria-expanded') === 'true';

        if (!this.allowMultiple && !isOpen) {
          this.querySelectorAll('.accordion__trigger[aria-expanded="true"]').forEach((other) => {
            other.setAttribute('aria-expanded', 'false');
            var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
            if (otherPanel) otherPanel.hidden = true;
          });
        }

        trigger.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
      }
    }
  );

  /* -------------------------------------------------------- quantity input */

  define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (!this.input) return;

        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);

        this.addEventListener('click', this.onClick);
        this.input.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.input) this.input.removeEventListener('change', this.onChange);
      }

      onClick(event) {
        var button = event.target.closest('button[name]');
        if (!button) return;
        event.preventDefault();

        var step = button.name === 'plus' ? 1 : -1;
        var current = parseInt(this.input.value, 10) || 0;
        var min = parseInt(this.input.min, 10);
        var max = parseInt(this.input.max, 10);
        var next = current + step;

        if (!isNaN(min) && next < min) next = min;
        if (!isNaN(max) && next > max) next = max;

        if (next !== current) {
          this.input.value = String(next);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onChange() {
        var min = parseInt(this.input.min, 10);
        var max = parseInt(this.input.max, 10);
        var value = parseInt(this.input.value, 10);

        if (isNaN(value)) value = isNaN(min) ? 1 : min;
        if (!isNaN(min) && value < min) value = min;
        if (!isNaN(max) && value > max) value = max;

        this.input.value = String(value);
      }
    }
  );

  /* ------------------------------------------------------------- cart items */

  function renderCartError(scope, message) {
    var node = scope.querySelector('[data-line-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  function replaceCartMarkup(html) {
    // The Section Rendering API wraps the response in <div id="shopify-section-…">.
    // Inject its contents rather than the wrapper, so the refreshed DOM matches
    // the server-rendered shape — an extra block here breaks the panel's flex
    // column and pushes the footer past the bottom of the drawer.
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var section = doc.querySelector('#shopify-section-cart-drawer');
    var markup = section ? section.innerHTML : doc.body.innerHTML;

    document.querySelectorAll('[data-cart-drawer-contents]').forEach(function (target) {
      target.innerHTML = markup;
    });
  }

  class CartUpdater extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener(CART_UPDATED, this.onCartUpdated);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener(CART_UPDATED, this.onCartUpdated);
    }

    onCartUpdated(event) {
      // Another component changed the cart; pull fresh markup for this view.
      if (event.detail && event.detail.source === this) return;
      this.refresh();
    }

    onClick(event) {
      var removeGroup = event.target.closest('[data-cart-remove-group]');
      if (removeGroup) {
        event.preventDefault();
        this.changeGroup(
          removeGroup.getAttribute('data-cart-remove-group'),
          0,
          removeGroup.closest('[data-cart-plan-group]')
        );
        return;
      }

      var remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      this.change(remove.getAttribute('data-cart-remove'), 0, remove.closest('[data-cart-line]'));
    }

    onChange(event) {
      var note = event.target.closest('[data-cart-note]');
      if (note) {
        this.saveNote(note.value);
        return;
      }

      var groupInput = event.target.closest('input[data-plan-group]');
      if (groupInput) {
        var groupQuantity = parseInt(groupInput.value, 10);
        if (isNaN(groupQuantity) || groupQuantity < 0) return;
        this.changeGroup(
          groupInput.getAttribute('data-plan-group'),
          groupQuantity,
          groupInput.closest('[data-cart-plan-group]')
        );
        return;
      }

      var input = event.target.closest('input[data-line-key]');
      if (!input) return;
      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) return;
      this.change(input.getAttribute('data-line-key'), quantity, input.closest('[data-cart-line]'));
    }

    // A grouped plan is one thing to the customer, so its lines move together.
    // /cart/update.js takes every line in a single call, which also avoids the
    // line numbers shifting underneath us between sequential removals.
    changeGroup(planLabel, quantity, scope) {
      var container = this.querySelector('[data-cart-items]') || this;
      container.setAttribute('aria-busy', 'true');
      if (scope) renderCartError(scope, '');

      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(
          function (cart) {
            var updates = {};
            (cart.items || []).forEach(function (item) {
              var label = item.properties && item.properties.Plan;
              updates[item.key] = label === planLabel ? quantity : item.quantity;
            });

            return fetch(routes.cart_update + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates })
            });
          }.bind(this)
        )
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(
          function (cart) {
            announce(quantity === 0 ? strings.removedAnnouncement : strings.updatedAnnouncement);
            publishCartUpdate(cart, this);
            refreshCartCount();
            return this.refresh();
          }.bind(this)
        )
        .catch(function (error) {
          var message = (error && error.description) || strings.cartError || 'Something went wrong.';
          if (scope) renderCartError(scope, message);
          announce(message);
        })
        .finally(function () {
          container.removeAttribute('aria-busy');
        });
    }

    // The note lives outside the checkout form, so it has to be persisted
    // through the Cart API rather than submitted with it.
    saveNote(value) {
      fetch(routes.cart_update + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      }).catch(function () {
        /* the note is not worth blocking checkout over */
      });
    }

    // A bundle app can rewrite line properties after the cart renders, which
    // changes Shopify's line key and makes the rendered one dead. So resolve the
    // line against the live cart at click time and address it by position, which
    // survives a key change.
    resolveLine(lineKey) {
      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          var items = cart.items || [];
          var index = items.findIndex(function (item) {
            return item.key === lineKey;
          });
          if (index === -1) {
            // Key is stale. Fall back to the variant, which the key starts with.
            var variantId = String(lineKey).split(':')[0];
            index = items.findIndex(function (item) {
              return String(item.variant_id) === variantId;
            });
          }
          return index === -1 ? null : index + 1;
        })
        .catch(function () {
          return null;
        });
    }

    change(lineKey, quantity, scope) {
      var container = this.querySelector('[data-cart-items]') || this;
      container.setAttribute('aria-busy', 'true');
      if (scope) renderCartError(scope, '');

      this.resolveLine(lineKey)
        .then(function (line) {
          var payload = line ? { line: line, quantity: quantity } : { id: lineKey, quantity: quantity };
          return fetch(routes.cart_change + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          });
        })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(
          function (cart) {
            announce(quantity === 0 ? strings.removedAnnouncement : strings.updatedAnnouncement);
            publishCartUpdate(cart, this);
            refreshCartCount();
            return this.refresh();
          }.bind(this)
        )
        .catch(function (error) {
          var message = (error && error.description) || strings.cartError || 'Something went wrong.';
          if (scope) renderCartError(scope, message);
          announce(message);
        })
        .finally(function () {
          container.removeAttribute('aria-busy');
        });
    }

    refresh() {
      var id = this.sectionId;
      if (!id) return Promise.resolve();

      return fetchSection(id, window.location.pathname)
        .then(
          function (doc) {
            var fresh = doc.querySelector('#shopify-section-' + id) || doc.querySelector('cart-items');
            var current = document.getElementById('shopify-section-' + id);
            if (fresh && current) {
              current.innerHTML = fresh.innerHTML;
            }
          }.bind(this)
        )
        .catch(function () {
          /* leave the current markup in place */
        });
    }
  }

  define('cart-items', CartUpdater);

  define(
    'cart-drawer',
    class CartDrawer extends CartUpdater {
      connectedCallback() {
        this.sectionId = 'cart-drawer';
        super.connectedCallback();
      }

      refresh() {
        return fetch(routes.root + '?section_id=cart-drawer')
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            replaceCartMarkup(html);
          })
          .catch(function () {});
      }
    }
  );

  /* ----------------------------------------------------------- product form */

  define(
    'product-form',
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.errorNode = this.querySelector('.product-form__error');
        this.submitButton = this.querySelector('[data-add-button]') || this.querySelector('button[name="add"]');
        this.variantInput = this.querySelector('[data-variant-id]');
        this.variants = this.parseVariants();
        this.planPrices = this.parseJson('[data-selling-plan-json]') || {};
        // Present only where larger packs are separate products, in which case
        // each tier carries its own variant and its own selling plans.
        this.packs = this.parseJson('[data-pack-json]');
        // Present on plan products, which sell themselves as their components so
        // the order carries SKUs the fulfilment service can actually ship.
        this.planComponents = this.parseJson('[data-plan-components-json]');

        this.onSubmit = this.onSubmit.bind(this);
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onPurchaseModeChange = this.onPurchaseModeChange.bind(this);
        this.onPricingChange = this.onPricingChange.bind(this);

        this.form.addEventListener('submit', this.onSubmit);
        this.addEventListener('change', this.onOptionChange);
        this.addEventListener('change', this.onPurchaseModeChange);
        this.addEventListener('change', this.onPricingChange);

        if (this.packs) this.applyPack();
        this.refreshPricing();
      }

      /* --- pack tiers backed by separate products ---------------------------- */

      currentPack() {
        if (!this.packs) return null;
        var tier = this.querySelector('[data-pack-count]:checked');
        var count = tier ? tier.value : Object.keys(this.packs)[0];
        return this.packs[String(count)] || null;
      }

      // The cadence label, not the plan id: the same cadence is a different plan
      // on each pack product.
      selectedCadence() {
        var plan = this.querySelector('input[name="selling_plan"]:checked');
        return plan && !plan.disabled ? plan.dataset.cadence || '' : '';
      }

      planForPack(pack) {
        if (!pack || !pack.plans) return null;
        var cadence = this.selectedCadence();
        return cadence ? pack.plans[cadence] || null : null;
      }

      packPricing(pack) {
        if (!pack) return null;
        var price = pack.price;
        var compare = pack.compare_at || 0;

        if (this.isSubscription()) {
          var allocation = this.planForPack(pack);
          if (allocation) {
            price = allocation.price;
            compare = Math.max(compare, allocation.compare_at || 0);
          }
        }

        return { price: price, compare: compare > price ? compare : null };
      }

      // Point the form at the chosen tier's product variant.
      applyPack() {
        var pack = this.currentPack();
        if (!pack) return;

        if (this.variantInput) {
          this.variantInput.value = pack.variant;
          this.variantInput.disabled = !pack.available;
        }
        if (this.submitButton) this.submitButton.disabled = !pack.available;
      }

      /* --- plans that ship separate component products ------------------------ */

      selectedPackCount() {
        var tier = this.querySelector('[data-pack-count]:checked');
        return tier ? String(tier.dataset.packCount) : '1';
      }

      // The component set for the current choices. The map is keyed
      // "<option value>|<units>", so match on whichever checked option value the
      // map actually knows about rather than assuming an option position.
      currentPlanComponents() {
        if (!this.planComponents) return null;

        var count = this.selectedPackCount();
        var chosen = [];
        this.querySelectorAll('[data-option-position]:checked').forEach(function (input) {
          chosen.push(input.value);
        });

        for (var i = 0; i < chosen.length; i++) {
          var key = chosen[i] + '|' + count;
          if (this.planComponents[key]) return { key: key, choice: chosen[i], components: this.planComponents[key] };
        }

        // No option dimension, e.g. a plan with a single configuration.
        var soleKey = '|' + count;
        if (this.planComponents[soleKey]) {
          return { key: soleKey, choice: '', components: this.planComponents[soleKey] };
        }
        return null;
      }

      // What the order line says, so a plan sale is still recognisable as one.
      planLabel(match) {
        var unit = this.querySelector('[data-pack-count]:checked');
        var unitText = unit ? (this.querySelector('label[for="' + unit.id + '"] .variant-option__name') || {}).textContent : '';
        var parts = [this.dataset.planName || document.title];
        if (match.choice) parts.push(match.choice);
        if (unitText) parts.push(unitText.trim());
        return parts.join(' · ');
      }

      // Which selling plan actually gets submitted.
      resolvedPlanId() {
        if (!this.isSubscription()) return null;
        if (this.packs) {
          var allocation = this.planForPack(this.currentPack());
          return allocation ? allocation.id : null;
        }
        var chosen = this.selectedPlan();
        return chosen ? chosen.value : null;
      }

      parseJson(selector) {
        var node = this.querySelector(selector);
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      }

      currentVariant() {
        if (!this.variants || !this.variantInput) return null;
        var id = parseInt(this.variantInput.value, 10);
        return this.variants.find(function (v) {
          return v.id === id;
        }) || null;
      }

      isSubscription() {
        var mode = this.querySelector('[data-purchase-mode]:checked');
        return Boolean(mode) && mode.dataset.purchaseMode === 'subscription';
      }

      // Subscribe mode is only real if a plan will actually be submitted. A pack
      // product with no plan for the chosen cadence sells as a one-time purchase,
      // and the copy must say so rather than promising a subscription.
      subscriptionActive() {
        if (!this.isSubscription()) return false;
        if (this.packs) return Boolean(this.planForPack(this.currentPack()));
        return Boolean(this.selectedPlan());
      }

      selectedQuantity() {
        // A pack option prices multiple units inside one variant, so the cart
        // quantity stays 1 and the pack size only affects the per-unit label.
        if (this.querySelector('[data-pack-count]')) return 1;
        var tier = this.querySelector('[data-quantity-tier]:checked');
        if (tier) return parseInt(tier.value, 10) || 1;
        var input = this.querySelector('input[name="quantity"]');
        return input ? parseInt(input.value, 10) || 1 : 1;
      }

      // Price of a specific variant under the current purchase mode.
      pricingFor(variant) {
        if (!variant) return null;
        var price = variant.price;
        var compare = variant.compare_at_price || 0;

        if (this.isSubscription()) {
          var plan = this.selectedPlan();
          var forVariant = plan ? this.planPrices[String(variant.id)] : null;
          var allocation = forVariant ? forVariant[String(plan.value)] : null;
          if (allocation) {
            price = allocation.price;
            // The higher of the two: a multi-pack compares against buying the
            // units singly, which beats comparing against the pack's own price.
            compare = Math.max(compare, allocation.compare_at || 0);
          }
        }

        return { price: price, compare: compare > price ? compare : null };
      }

      variantForOptionValue(position, value) {
        if (!this.variants) return null;
        var selected = this.selectedOptions();
        selected[position - 1] = value;
        return this.variants.find(function (v) {
          return v.options.every(function (option, index) {
            return selected[index] === undefined || selected[index] === option;
          });
        }) || null;
      }

      selectedPlan() {
        var plan = this.querySelector('input[name="selling_plan"]:checked');
        return plan && !plan.disabled ? plan : null;
      }

      // One place decides the unit price, so the tiers, the price row and the
      // button label can never disagree with each other.
      // A plan's one-time price is whatever its components add up to, because the
      // components are what the cart actually charges. The subscription price
      // stays on the plan variant, which is what the bundle discount targets.
      componentOneTimePricing() {
        var match = this.currentPlanComponents();
        if (!match || !match.components.length) return null;

        var total = match.components.reduce(function (sum, component) {
          return sum + component.price;
        }, 0);

        var variant = this.currentVariant();
        var compare = variant && variant.compare_at_price ? variant.compare_at_price : 0;
        return { price: total, compare: compare > total ? compare : null };
      }

      unitPricing() {
        if (this.planComponents && !this.isSubscription()) {
          var componentPricing = this.componentOneTimePricing();
          if (componentPricing) return componentPricing;
        }
        if (this.packs) return this.packPricing(this.currentPack());
        return this.pricingFor(this.currentVariant());
      }

      onPricingChange(event) {
        if (!event.target.matches('[data-quantity-tier], [data-pack-count], input[name="selling_plan"], [data-purchase-mode]')) {
          return;
        }
        // The tier, the cadence and the mode all change which variant is bought.
        if (this.packs) this.applyPack();
        this.refreshPricing();
      }

      refreshPricing() {
        var unit = this.unitPricing();
        if (!unit) return;

        var quantity = this.selectedQuantity();

        var eachTemplate = strings.each || '[amount] each';

        // Pack tiers each price their own variant; plain quantity tiers all
        // share the selected variant's unit price.
        this.querySelectorAll('[data-pack-count]').forEach(
          function (pack) {
            var label = this.querySelector('label[for="' + pack.id + '"] [data-tier-unit-price]');
            if (!label) return;
            var count = parseInt(pack.dataset.packCount, 10) || 1;
            var packPricing = this.packs
              ? this.packPricing(this.packs[String(count)])
              : this.pricingFor(this.variantForOptionValue(parseInt(pack.dataset.optionPosition, 10), pack.value));
            if (!packPricing) {
              label.textContent = '';
              return;
            }
            label.textContent = eachTemplate.replace('[amount]', formatMoney(Math.round(packPricing.price / count)));
          }.bind(this)
        );

        this.querySelectorAll('[data-quantity-tier]').forEach(
          function (tier) {
            var label = this.querySelector('label[for="' + tier.id + '"] [data-tier-unit-price]');
            if (label) label.textContent = eachTemplate.replace('[amount]', formatMoney(unit.price));
          }.bind(this)
        );

        var container = this.querySelector('[data-price-container] .price');
        if (container) {
          var regular = container.querySelector('.price__regular');
          var sale = container.querySelector('.price__sale');
          var compare = container.querySelector('.price__compare');
          var total = unit.price * quantity;
          var compareTotal = unit.compare ? unit.compare * quantity : 0;
          var onSale = compareTotal > total;

          container.classList.toggle('price--on-sale', onSale);
          if (regular) regular.textContent = formatMoney(total);
          if (sale) sale.textContent = formatMoney(total);
          if (compare) {
            compare.textContent = onSale ? formatMoney(compareTotal) : '';
            compare.hidden = !onSale;
          }

          var badge = this.querySelector('[data-price-container] .save-badge');
          if (badge) {
            badge.hidden = !onSale;
            // Cleared rather than left stale, so switching to one-time cannot
            // leave a saving amount behind in the markup.
            badge.textContent = onSale
              ? (strings.saveAmount || 'Save [amount]').replace('[amount]', formatMoney(compareTotal - total))
              : '';
          }
        }

        this.refreshButtonLabel(unit.price * quantity);
        this.refreshPlanCopy(unit, quantity);
      }

      refreshButtonLabel(total) {
        if (!this.submitButton) return;
        var label = this.submitButton.querySelector('[data-add-button-text]');
        if (!label || !label.dataset.dynamic) return;

        if (this.packs) {
          var pack = this.currentPack();
          if (pack && !pack.available) return;
        } else {
          var variant = this.currentVariant();
          if (variant && !variant.available) return;
        }

        if (this.subscriptionActive()) {
          var plan = this.selectedPlan();
          var cadence = plan ? (plan.dataset.cadence || '') : '';
          label.textContent = (strings.subscribeCta || 'Subscribe · [price] [cadence]')
            .replace('[price]', formatMoney(total))
            .replace('[cadence]', cadence)
            .trim();
        } else {
          label.textContent = (strings.addToCartPrice || 'Add to cart · [price]').replace(
            '[price]',
            formatMoney(total)
          );
        }
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        this.removeEventListener('change', this.onOptionChange);
        this.removeEventListener('change', this.onPurchaseModeChange);
      }

      parseVariants() {
        var script = this.querySelector('[data-variant-json]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }

      selectedOptions() {
        var values = [];
        this.querySelectorAll('[data-option-position]:checked').forEach(function (input) {
          values[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
        });
        return values;
      }

      onOptionChange(event) {
        if (!event.target.matches('[data-option-position]')) return;
        if (!this.variants) return;

        var selected = this.selectedOptions();
        var match = this.variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            return selected[index] === undefined || selected[index] === option;
          });
        });

        this.updateStackedTicks();

        if (!match) {
          this.setUnavailable();
          return;
        }

        if (this.variantInput) {
          this.variantInput.value = match.id;
          this.variantInput.disabled = !match.available;
        }

        this.updatePrice(match);
        this.updateButton(match);
        this.updateSku(match);
        this.updateUrl(match);
        this.refreshPricing();
      }

      updateStackedTicks() {
        this.querySelectorAll('.variant-option__values--stacked').forEach(function (group) {
          group.querySelectorAll('.variant-option__input').forEach(function (input) {
            var mark = group.querySelector('label[for="' + input.id + '"] .variant-option__mark');
            if (mark) mark.innerHTML = input.checked ? '&#10003;' : '';
          });
        });
      }

      updatePrice(variant) {
        var container = this.querySelector('[data-price-container] .price');
        if (!container) return;

        var regular = container.querySelector('.price__regular');
        var sale = container.querySelector('.price__sale');
        var compare = container.querySelector('.price__compare');
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        container.classList.toggle('price--on-sale', Boolean(onSale));

        if (regular) regular.textContent = formatMoney(variant.price);
        if (sale) sale.textContent = formatMoney(variant.price);
        if (compare) {
          compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
          compare.hidden = !onSale;
        }

        var saveBadge = this.querySelector('[data-price-container] .save-badge');
        if (saveBadge) {
          saveBadge.hidden = !onSale;
          if (onSale) {
            saveBadge.textContent = formatMoney(variant.compare_at_price - variant.price);
          }
        }
      }

      updateButton(variant) {
        if (!this.submitButton) return;
        var label = this.submitButton.querySelector('[data-add-button-text]') || this.submitButton;
        this.submitButton.disabled = !variant.available;
        label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }

      updateSku(variant) {
        var node = this.querySelector('[data-sku]');
        if (node) node.textContent = variant.sku || '';
      }

      updateUrl(variant) {
        if (this.dataset.updateUrl !== 'true' || !this.dataset.productUrl) return;
        window.history.replaceState({}, '', this.dataset.productUrl + '?variant=' + variant.id);
      }

      setUnavailable() {
        if (!this.submitButton) return;
        var label = this.submitButton.querySelector('[data-add-button-text]') || this.submitButton;
        this.submitButton.disabled = true;
        label.textContent = strings.unavailable;
      }

      onPurchaseModeChange(event) {
        if (!event.target.matches('[data-purchase-mode]')) return;

        var subscription = event.target.dataset.purchaseMode === 'subscription';
        var group = this.querySelector('[data-delivery-frequency]');
        if (group) group.hidden = !subscription;

        this.querySelectorAll('input[name="selling_plan"]').forEach(function (input) {
          input.disabled = !subscription;
        });

        this.refreshPricing();
      }

      // The saving and the cadence are derived from the selected plan, so they
      // always agree with the price shown beside them.
      refreshPlanCopy(unit, quantity) {
        var subscription = this.subscriptionActive();

        var note = this.querySelector('[data-plan-note]');
        if (note) {
          var saving = unit.compare && unit.compare > unit.price ? (unit.compare - unit.price) * quantity : 0;
          if (!subscription) {
            note.textContent = strings.oneTimeNote || '';
          } else if (saving > 0 && strings.subscriptionSaves) {
            note.textContent = strings.subscriptionSaves.replace('[amount]', formatMoney(saving));
          } else {
            note.textContent = strings.subscriptionNote || '';
          }
        }

        var cadenceNode = this.querySelector('[data-cadence-label]');
        if (!cadenceNode) return;

        if (!subscription) {
          cadenceNode.textContent = strings.oneTimePurchase || cadenceNode.dataset.fallback || '';
          return;
        }

        var plan = this.selectedPlan();
        var cadence = plan ? plan.dataset.cadence : '';
        if (cadence && strings.shipsEvery) {
          cadenceNode.textContent = strings.shipsEvery.replace('[cadence]', cadence);
        } else if (cadenceNode.dataset.fallback) {
          cadenceNode.textContent = cadenceNode.dataset.fallback;
        }
      }

      // The ordinary case: this product's own variant.
      variantRequest() {
        var body = new FormData(this.form);

        // A subscription app may inject its own selling_plan field into this
        // form. Set the value from our own control so what is submitted always
        // matches the price and cadence shown on the page.
        body.delete('selling_plan');
        body.delete('pack_size');
        var planId = this.resolvedPlanId();
        if (planId) body.set('selling_plan', planId);

        body.append('sections_url', window.location.pathname);

        return {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: body
        };
      }

      // A plan: submit the products that actually ship, one line each, so the
      // order carries real fulfilment SKUs instead of the plan's own.
      componentRequest() {
        var match = this.currentPlanComponents();
        if (!match || !match.components.length) return null;

        var cadence = this.selectedCadence();
        var subscribing = this.isSubscription();
        var label = this.planLabel(match);

        // The underscored property is hidden from the customer; the cart uses it
        // to show the plan's own image and link rather than a component's.
        var planHandle = this.dataset.planHandle || '';

        var items = match.components.map(function (component) {
          var item = {
            id: component.variant,
            quantity: 1,
            properties: { Plan: label, _plan_handle: planHandle }
          };
          // Each component keeps its own plan, since they can sit in different
          // selling plan groups.
          if (subscribing && cadence && component.plans && component.plans[cadence]) {
            item.selling_plan = component.plans[cadence];
          }
          return item;
        });

        return {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: items, sections_url: window.location.pathname })
        };
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.setError('');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('is-loading');

        var request = this.planComponents ? this.componentRequest() : this.variantRequest();
        if (!request) {
          this.setError(strings.cartError || '');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('is-loading');
          return;
        }

        fetch(routes.cart_add + '.js', request)
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return data;
            });
          })
          .then(
            function () {
              this.setError('');
              announce(strings.addedToCart);
              refreshCartCount();
              publishCartUpdate(null);

              if (window.Brewell.cartType === 'drawer') {
                var drawer = document.getElementById('CartDrawer');
                var cartDrawer = document.querySelector('cart-drawer');
                var reopen = function () {
                  if (drawer) DrawerManager.show(drawer);
                };
                if (cartDrawer && typeof cartDrawer.refresh === 'function') {
                  cartDrawer.refresh().then(reopen);
                } else {
                  reopen();
                }
              } else {
                window.location.href = routes.cart;
              }
            }.bind(this)
          )
          .catch(
            function (error) {
              var message = (error && error.description) || strings.cartError;
              this.setError(message);
              announce(message);
            }.bind(this)
          )
          .finally(
            function () {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.classList.remove('is-loading');
            }.bind(this)
          );
      }

      setError(message) {
        if (!this.errorNode) return;
        var target = this.errorNode.querySelector('[data-error-message]') || this.errorNode;
        target.textContent = message || '';
        this.errorNode.hidden = !message;
      }
    }
  );

  /* --------------------------------------------------------- product gallery */

  define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.addEventListener('click', this.onClick);

        this.track = this.querySelector('[data-gallery-main]');
        if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
      }

      /* The same markup is a switcher on desktop and a swipe track on mobile.
         Rather than read a breakpoint in JS and risk it drifting from the CSS,
         ask the layout: a track is the thing that actually overflows. */
      isTrack() {
        return !!this.track && this.track.scrollWidth > this.track.clientWidth + 4;
      }

      setCurrent(mediaId) {
        this.querySelectorAll('[data-gallery-thumb]').forEach(function (node) {
          node.setAttribute('aria-current', String(node.getAttribute('data-gallery-thumb') === mediaId));
        });
      }

      onClick(event) {
        var thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb) return;

        var mediaId = thumb.getAttribute('data-gallery-thumb');

        if (this.isTrack()) {
          /* Thumbnails drive the track instead of hiding slides, because on a
             track every slide has to stay in flow. */
          var slide = this.querySelector('[data-media-id="' + mediaId + '"]');
          if (slide) {
            var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            /* Measured, not offsetLeft: the slide's offsetParent is not
               guaranteed to be the track, and when it is not the target lands
               short. Scroll snap was quietly correcting for that. */
            this.track.scrollTo({
              left:
                this.track.scrollLeft +
                slide.getBoundingClientRect().left -
                this.track.getBoundingClientRect().left,
              behavior: reduced ? 'auto' : 'smooth'
            });
          }
          this.setCurrent(mediaId);
          return;
        }

        this.querySelectorAll('[data-media-id]').forEach(function (node) {
          node.hidden = node.getAttribute('data-media-id') !== mediaId;
        });
        this.setCurrent(mediaId);
      }

      /* Swiping has to move the thumbnail highlight too, or the two controls
         disagree about which image you are looking at. */
      onScroll() {
        if (!this.isTrack()) return;
        if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(
          function () {
            var trackBox = this.track.getBoundingClientRect();
            var mid = trackBox.left + trackBox.width / 2;
            var current = null;
            var best = Infinity;
            this.querySelectorAll('[data-media-id]').forEach(function (slide) {
              var box = slide.getBoundingClientRect();
              var dist = Math.abs(box.left + box.width / 2 - mid);
              if (dist < best) {
                best = dist;
                current = slide.getAttribute('data-media-id');
              }
            }, this);
            if (current) this.setCurrent(current);
          }.bind(this),
          80
        );
      }
    }
  );

  /* ------------------------------------------------------- predictive search */

  define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        if (this.dataset.enabled !== 'true') return;

        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-predictive-results]');
        this.status = this.querySelector('[data-predictive-status]');
        if (!this.input || !this.results) return;

        this.timer = null;
        this.controller = null;

        this.onInput = this.onInput.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        this.input.addEventListener('input', this.onInput);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        if (this.controller) this.controller.abort();
        if (this.input) this.input.removeEventListener('input', this.onInput);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      onInput() {
        window.clearTimeout(this.timer);
        var term = this.input.value.trim();

        if (term.length < 2) {
          this.close();
          return;
        }

        this.timer = window.setTimeout(this.search.bind(this, term), 250);
      }

      search(term) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        var url =
          routes.predictive_search +
          '?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,page,article,collection&resources[limit]=5&section_id=predictive-search';

        fetch(url, { signal: this.controller.signal })
          .then(function (response) {
            return response.text();
          })
          .then(
            function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var body = doc.querySelector('.shopify-section') || doc.body;
              this.results.innerHTML = body.innerHTML;
              this.open();
            }.bind(this)
          )
          .catch(function () {
            /* aborted or offline — leave the previous results */
          });
      }

      open() {
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        if (this.status) this.status.textContent = strings.predictiveResults || '';
      }

      close() {
        if (!this.results) return;
        this.results.hidden = true;
        if (this.input) this.input.setAttribute('aria-expanded', 'false');
      }
    }
  );

  /* --------------------------------------------------------------- facets */

  define(
    'facet-filters',
    class FacetFilters extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.sectionId = this.dataset.sectionId;
        if (!this.form) return;

        this.onChange = this.onChange.bind(this);
        this.form.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('change', this.onChange);
      }

      onChange() {
        var params = new URLSearchParams(new FormData(this.form)).toString();
        var url = window.location.pathname + (params ? '?' + params : '');

        this.setAttribute('aria-busy', 'true');

        fetchSection(this.sectionId, url)
          .then(
            function (doc) {
              var fresh = doc.getElementById('shopify-section-' + this.sectionId);
              var current = document.getElementById('shopify-section-' + this.sectionId);
              if (fresh && current) {
                current.innerHTML = fresh.innerHTML;
                window.history.pushState({ url: url }, '', url);
              }
            }.bind(this)
          )
          .catch(
            function () {
              window.location.href = url;
            }
          )
          .finally(
            function () {
              this.removeAttribute('aria-busy');
            }.bind(this)
          );
      }
    }
  );

  window.addEventListener('popstate', function () {
    var facets = document.querySelector('facet-filters');
    if (facets) window.location.reload();
  });

  /* ----------------------------------------------------------------- quiz */

  var STORAGE_KEY = 'brewell:quiz';

  define(
    'product-quiz',
    class ProductQuiz extends HTMLElement {
      connectedCallback() {
        this.total = parseInt(this.dataset.total, 10) || 0;
        this.answers = [];
        this.step = 0;

        this.questions = Array.prototype.slice.call(this.querySelectorAll('[data-quiz-question]'));
        this.stages = {
          questions: this.querySelector('[data-quiz-stage="questions"]'),
          gate: this.querySelector('[data-quiz-stage="gate"]'),
          results: this.querySelector('[data-quiz-stage="results"]')
        };
        this.counter = this.querySelector('[data-quiz-counter]');
        this.progress = this.querySelector('[data-quiz-progress]');
        this.backButton = this.querySelector('[data-quiz-back]');

        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.addEventListener('click', this.onClick);

        this.gateForm = this.querySelector('[data-quiz-stage="gate"] form');
        if (this.gateForm) this.gateForm.addEventListener('submit', this.onSubmit);

        this.render();
        this.restore();
      }

      // The email gate is a real Shopify customer form, so submitting it reloads
      // the page. Pick the answers back up and jump straight to the result.
      restore() {
        var stored = null;
        try {
          stored = window.sessionStorage.getItem(STORAGE_KEY);
          window.sessionStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          return;
        }
        if (!stored) return;

        try {
          this.answers = JSON.parse(stored) || [];
        } catch (error) {
          return;
        }

        this.step = this.total;

        var status = this.querySelector('[data-quiz-status]');
        var failed = status && status.textContent.trim() !== '';

        if (failed && this.stages.gate) {
          // Shopify rejected the address — keep them on the gate to retry.
          this.showStage('gate');
          try {
            window.sessionStorage.setItem(STORAGE_KEY, stored);
          } catch (error) {
            /* private mode — the retry simply will not persist */
          }
          return;
        }

        // Coming back from a successful gate submit is the one path that earns
        // the code.
        this.setCouponVisible(true);
        this.showStage('results');
        this.showResult();
      }

      // The code is shown only to people who handed over an email. Skipping the
      // gate, or restarting, leaves it hidden.
      setCouponVisible(visible) {
        this.querySelectorAll('[data-quiz-coupon]').forEach(function (node) {
          node.hidden = !visible;
        });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.gateForm) this.gateForm.removeEventListener('submit', this.onSubmit);
      }

      onClick(event) {
        var answer = event.target.closest('[data-quiz-answer]');
        if (answer) {
          this.answers[this.step] = answer.getAttribute('data-quiz-answer');
          this.step += 1;
          if (this.step >= this.total) {
            this.showStage(this.stages.gate ? 'gate' : 'results');
            if (!this.stages.gate) {
              // No gate to pass, so nothing to withhold.
              this.setCouponVisible(true);
              this.showResult();
            }
          } else {
            this.render();
          }
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        if (event.target.closest('[data-quiz-back]')) {
          this.step = Math.max(0, this.step - 1);
          this.render();
          return;
        }

        if (event.target.closest('[data-quiz-skip]')) {
          event.preventDefault();
          this.setCouponVisible(false);
          this.showStage('results');
          this.showResult();
          return;
        }

        if (event.target.closest('[data-quiz-restart]')) {
          this.answers = [];
          this.step = 0;
          if (this.stages.gate) this.setCouponVisible(false);
          this.showStage('questions');
          this.render();
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      onSubmit() {
        // The form posts to Shopify's customer endpoint and reloads. Store the
        // answers so restore() can show the result when the page comes back.
        try {
          window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.answers));
        } catch (error) {
          /* private mode — the result simply will not persist */
        }
      }

      render() {
        this.questions.forEach(
          function (node, index) {
            node.hidden = index !== this.step;
          }.bind(this)
        );

        if (this.counter) {
          if (!this.counter.dataset.template) {
            this.counter.dataset.template = this.counter.textContent.trim();
          }
          this.counter.textContent = this.counter.dataset.template.replace(
            /\d+/,
            String(this.step + 1)
          );
        }

        if (this.progress) {
          var percent = this.total ? (this.step / this.total) * 100 : 0;
          this.progress.style.width = percent + '%';
          var bar = this.progress.parentElement;
          if (bar) bar.setAttribute('aria-valuenow', String(this.step));
        }

        if (this.backButton) this.backButton.hidden = this.step === 0;
      }

      showStage(name) {
        Object.keys(this.stages).forEach(
          function (key) {
            if (this.stages[key]) this.stages[key].hidden = key !== name;
          }.bind(this)
        );
      }

      showResult() {
        var tally = {};
        this.answers.forEach(function (key) {
          if (!key) return;
          tally[key] = (tally[key] || 0) + 1;
        });

        var results = Array.prototype.slice.call(this.querySelectorAll('[data-quiz-result]'));
        if (!results.length) return;

        var best = null;
        var bestScore = -1;

        results.forEach(function (node) {
          var keys = (node.getAttribute('data-quiz-keys') || '').split(',').map(function (k) {
            return k.trim();
          });
          var score = keys.reduce(function (sum, key) {
            return sum + (tally[key] || 0);
          }, 0);
          if (score > bestScore) {
            bestScore = score;
            best = node;
          }
        });

        results.forEach(function (node) {
          node.hidden = node !== best;
        });
      }
    }
  );

  /* -------------------------------------------------------- bundle builder */

  define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        this.slotCount = parseInt(this.dataset.slots, 10) || 3;
        this.discount = parseFloat(this.dataset.discountPercent) || 0;
        this.items = [];

        this.slots = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-slot]'));
        this.note = this.querySelector('[data-bundle-note]');
        this.totalNode = this.querySelector('[data-bundle-total]');
        this.compareNode = this.querySelector('[data-bundle-compare]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.errorNode = this.querySelector('[data-bundle-error]');

        // "+ Bundle" buttons live on product cards elsewhere on the page, so the
        // listener has to sit on the document rather than on this element.
        this.onClick = this.onClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onDocumentClick);

        this.render();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('click', this.onDocumentClick);
      }

      onDocumentClick(event) {
        var add = event.target.closest('[data-bundle-add]');
        if (add && !this.contains(add)) {
          event.preventDefault();
          this.addItem(add);
          return;
        }

        var clear = event.target.closest('[data-bundle-clear]');
        if (clear && !this.contains(clear)) {
          event.preventDefault();
          this.onClear();
        }
      }

      addItem(button) {
        var id = button.getAttribute('data-variant-id');
        if (!id) return;

        if (this.items.some(function (item) { return item.id === id; })) {
          this.setMessage((this.dataset.duplicateLabel || '').replace(/\[product\]/g, button.getAttribute('data-title') || ''));
          return;
        }
        if (this.items.length >= this.slotCount) {
          this.setMessage(this.dataset.fullLabel || '');
          return;
        }

        this.setMessage('');
        this.items.push({
          id: id,
          title: button.getAttribute('data-title'),
          price: parseInt(button.getAttribute('data-price'), 10) || 0
        });
        this.render();
        announce(this.note ? this.note.textContent : '');
      }

      setMessage(message) {
        if (!this.errorNode) return;
        this.errorNode.textContent = message || '';
        this.errorNode.hidden = !message;
        if (message) announce(message);
      }

      onClear() {
        this.items = [];
        this.setMessage('');
        this.render();
      }

      onClick(event) {
        var add = event.target.closest('[data-bundle-add]');
        if (add) {
          event.preventDefault();
          this.addItem(add);
          return;
        }

        var clear = event.target.closest('[data-bundle-clear]');
        if (clear) {
          event.preventDefault();
          this.onClear();
          return;
        }

        var remove = event.target.closest('[data-bundle-remove]');
        if (remove) {
          this.items.splice(parseInt(remove.getAttribute('data-bundle-remove'), 10), 1);
          this.setMessage('');
          this.render();
          return;
        }

        if (event.target.closest('[data-bundle-submit]')) {
          this.submit();
        }
      }

      render() {
        this.slots.forEach(
          function (slot, index) {
            var item = this.items[index];
            var label = slot.querySelector('[data-bundle-slot-label]');
            var removeButton = slot.querySelector('[data-bundle-remove]');

            slot.classList.toggle('is-filled', Boolean(item));
            if (label) {
              if (!label.dataset.emptyLabel) {
                label.dataset.emptyLabel = label.textContent.trim();
              }
              label.textContent = item ? item.title : label.dataset.emptyLabel;
            }
            if (removeButton) removeButton.hidden = !item;
          }.bind(this)
        );

        var raw = this.items.reduce(function (sum, item) {
          return sum + item.price;
        }, 0);
        var full = this.items.length === this.slotCount;
        var total = full ? Math.round(raw * (1 - this.discount / 100)) : raw;

        if (this.totalNode) this.totalNode.textContent = formatMoney(total);
        if (this.compareNode) {
          this.compareNode.hidden = !full || this.discount === 0;
          this.compareNode.textContent = formatMoney(raw);
        }
        if (this.note) {
          // The labels can repeat a token, so every replacement is global.
          this.note.textContent = full
            ? (this.dataset.discountLabel || '').replace(/\[percent\]/g, String(this.discount))
            : (this.dataset.slotsLabel || '')
                .replace(/\[filled\]/g, String(this.items.length))
                .replace(/\[total\]/g, String(this.slotCount));
        }
        if (this.submitButton) this.submitButton.disabled = !full;
      }

      submit() {
        if (this.items.length !== this.slotCount) return;

        var payload = {
          items: this.items.map(function (item) {
            return { id: item.id, quantity: 1 };
          })
        };

        if (this.submitButton) this.submitButton.setAttribute('aria-disabled', 'true');

        fetch(routes.cart_add + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return data;
            });
          })
          .then(
            function () {
              if (this.errorNode) this.errorNode.hidden = true;
              this.items = [];
              this.render();
              announce(strings.addedToCart);
              refreshCartCount();

              var cartDrawer = document.querySelector('cart-drawer');
              var drawer = document.getElementById('CartDrawer');
              if (cartDrawer && drawer && typeof cartDrawer.refresh === 'function') {
                cartDrawer.refresh().then(function () {
                  DrawerManager.show(drawer);
                });
              } else {
                window.location.href = routes.cart;
              }
            }.bind(this)
          )
          .catch(
            function (error) {
              if (!this.errorNode) return;
              this.errorNode.textContent = (error && error.description) || strings.cartError;
              this.errorNode.hidden = false;
            }.bind(this)
          )
          .finally(
            function () {
              if (this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            }.bind(this)
          );
      }
    }
  );

  /* ----------------------------------------------------------- share button */

  define(
    'share-button',
    class ShareButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.status = this.querySelector('[data-share-status]');
        if (!this.button) return;

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
      }

      onClick() {
        var url = this.button.getAttribute('data-share-url') || window.location.href;

        if (navigator.share) {
          navigator.share({ url: url, title: document.title }).catch(function () {});
          return;
        }

        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(
            function () {
              if (this.status) this.status.textContent = strings.copied || 'Link copied';
            }.bind(this)
          );
        }
      }
    }
  );

  /* ------------------------------------------------------- recommendations */

  function loadRecommendations(scope) {
    (scope || document).querySelectorAll('[data-recommendations][data-url]').forEach(function (node) {
      var url = node.getAttribute('data-url');
      if (!url || node.dataset.loaded === 'true') return;
      node.dataset.loaded = 'true';

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-recommendations]');
          if (fresh) node.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------- in-page anchor scrolling */

  function stickyHeaderOffset() {
    // The whole header group pins, so measure the group where it is the sticky
    // element and fall back to the header alone. Below 960px neither sticks, so
    // nothing overlaps the target.
    var header = document.querySelector('.header-wrapper--sticky');
    if (!header) return 0;

    var group = header.closest('.header-group');
    var pinned = group && window.getComputedStyle(group).position === 'sticky' ? group : header;
    if (window.getComputedStyle(pinned).position !== 'sticky') return 0;
    return Math.round(pinned.getBoundingClientRect().height);
  }

  function publishAnchorOffset() {
    // Shared with base.css so a direct #hash page load lands in the same place.
    document.documentElement.style.setProperty('--anchor-offset', stickyHeaderOffset() + 'px');
  }

  function scrollToAnchor(target) {
    var top = target.getBoundingClientRect().top + window.pageYOffset - stickyHeaderOffset();
    top = Math.max(0, Math.round(top));

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      window.scrollTo(0, top);
      return;
    }

    // Fixed duration rather than the browser's distance-based smooth scroll, so
    // a jump down the whole page still takes the same short moment.
    var start = window.pageYOffset;
    var distance = top - start;
    var duration = 500;
    var begun = null;
    var running = false;

    function step(now) {
      running = true;
      if (begun === null) begun = now;
      var p = Math.min((now - begun) / duration, 1);
      var eased = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      window.scrollTo(0, start + distance * eased);
      if (p < 1) window.requestAnimationFrame(step);
    }

    if (!window.requestAnimationFrame) {
      window.scrollTo(0, top);
      return;
    }

    window.requestAnimationFrame(step);

    // If frames never arrive (background tab, no compositor), still land there.
    window.setTimeout(function () {
      if (!running) window.scrollTo(0, top);
    }, 250);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href*="#"]');
    if (!link || link.hasAttribute('download') || link.target === '_blank') return;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }

    if (url.host !== window.location.host || url.pathname !== window.location.pathname) return;
    if (!url.hash || url.hash === '#') return;

    var target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    scrollToAnchor(target);
    window.history.replaceState(null, '', url.hash);

    // Keep keyboard users where the scroll landed (e.g. the skip link).
    if (target.hasAttribute('tabindex') || /^(a|button|input|select|textarea)$/i.test(target.tagName)) {
      target.focus({ preventScroll: true });
    }
  });

  window.addEventListener('resize', publishAnchorOffset);

  /* --------------------------------------------------------- miscellaneous */

  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-localization-select]');
    if (select && select.form) select.form.submit();
  });

  document.addEventListener('click', function (event) {
    var deleteButton = event.target.closest('[data-address-delete]');
    if (!deleteButton) return;
    event.preventDefault();

    if (window.confirm(deleteButton.getAttribute('data-confirm-message'))) {
      var form = document.createElement('form');
      form.method = 'post';
      form.action = deleteButton.getAttribute('data-address-delete');
      form.innerHTML = '<input type="hidden" name="_method" value="delete">';
      document.body.appendChild(form);
      form.submit();
    }
  });

  /* ------------------------------------------------------- theme editor API */

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      loadRecommendations(event.target);
    });

    document.addEventListener('shopify:section:select', function (event) {
      var drawer = event.target.querySelector('[data-drawer]');
      if (drawer) DrawerManager.show(drawer);
    });

    document.addEventListener('shopify:section:deselect', function () {
      DrawerManager.hide();
    });

    document.addEventListener('shopify:block:select', function (event) {
      var block = event.target;

      // Reveal an accordion panel when its block is selected.
      var trigger = block.querySelector('.accordion__trigger');
      if (trigger && trigger.getAttribute('aria-expanded') === 'false') {
        trigger.click();
      }

      // Keep a hidden quiz question visible while it is being edited.
      var quiz = block.closest('product-quiz');
      if (quiz && block.hasAttribute('data-quiz-question')) {
        quiz.querySelectorAll('[data-quiz-question]').forEach(function (node) {
          node.hidden = node !== block;
        });
      }

      var result = block.closest('product-quiz');
      if (result && block.hasAttribute('data-quiz-result')) {
        result.showStage('results');
        result.querySelectorAll('[data-quiz-result]').forEach(function (node) {
          node.hidden = node !== block;
        });
      }

      block.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    document.addEventListener('shopify:block:deselect', function (event) {
      var quiz = event.target.closest('product-quiz');
      if (quiz && typeof quiz.render === 'function' && event.target.hasAttribute('data-quiz-question')) {
        quiz.showStage('questions');
        quiz.render();
      }
    });
  }

  /* --------------------------------------------------------- scroll reveal */

  /*
   * One observer for the whole document. Opt-in by [data-reveal] so sections
   * choose to animate rather than everything animating by default.
   *
   * Reveal is the weakest category of motion, because nothing actually
   * changed: the reader only scrolled. It earns its place here on one
   * ground, that it makes a long argument read as a sequence of statements
   * rather than a wall. So it reveals GROUPS OF CLAIMS and never a control
   * someone is trying to use. The purchase box, price, variant pickers and
   * cart contents are deliberately excluded.
   */
  function scrollReveal(scope) {
    var root = scope || document;
    var targets = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!targets.length) return;

    function showAll() {
      Array.prototype.forEach.call(targets, function (el) {
        el.classList.add('is-revealed');
      });
    }

    /* Any path that cannot animate must still show the content. */
    if (
      document.documentElement.classList.contains('motion-off') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof window.IntersectionObserver === 'undefined'
    ) {
      showAll();
      return;
    }

    if (!scrollReveal.observer) {
      scrollReveal.observer = new IntersectionObserver(
        function (entries, obs) {
          /* A working observer always delivers an initial callback for every
             element it is given, intersecting or not. Recording that is how we
             tell "nothing has scrolled into view yet" apart from "this browser
             is not delivering entries at all". */
          scrollReveal.delivered = true;
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            /* One-shot. Re-hiding on scroll-up is motion with no cause. */
            obs.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
      );
    }

    Array.prototype.forEach.call(targets, function (el) {
      scrollReveal.observer.observe(el);
    });

    /* Anything already on screen at load should not wait for a scroll that
       may never come on a short page. */
    window.setTimeout(function () {
      Array.prototype.forEach.call(targets, function (el) {
        if (el.classList.contains('is-revealed')) return;
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add('is-revealed');
          scrollReveal.observer.unobserve(el);
        }
      });
    }, 300);

    /* Last resort. If the observer has not delivered a single entry by now it
       is not going to, and the cost of being wrong here is a blank page. Show
       everything and stop pretending this browser can animate. */
    window.setTimeout(function () {
      if (scrollReveal.delivered) return;
      showAll();
      if (scrollReveal.observer) scrollReveal.observer.disconnect();
    }, 1500);
  }

  /* ----------------------------------------------------- sticky add to cart */

  /*
   * A proxy for the real add-to-cart button, never a second buy form.
   *
   * ProductForm owns the variant map, the selling-plan allocations, the
   * subscribe/one-time mode, the pack tiers and the component splitting. All of
   * that is resolved from live DOM state inside its own submit handler, so
   * forwarding a click is enough to get the exact cart line the shopper
   * configured. Nothing is recomputed here, which is why nothing here can
   * disagree with the form.
   */
  define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.target = document.querySelector('product-form [data-add-button]');
        this.button = this.querySelector('[data-sticky-add]');
        this.label = this.querySelector('[data-sticky-add-text]');
        this.cadence = this.querySelector('[data-sticky-cadence]');

        /* No purchase box on this template: stay hidden and inert. */
        if (!this.target || !this.button) return;

        this.targetLabel = this.target.querySelector('[data-add-button-text]');
        this.targetCadence = document.querySelector('[data-cadence-label]');

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        this.syncLabel();
        this.syncState();
        this.syncCadence();

        /* Mirror the label the form writes. It already carries the live price,
           so mirroring it is also how the bar stays price-accurate. */
        if (this.targetLabel && 'MutationObserver' in window) {
          this.labelObserver = new MutationObserver(this.syncLabel.bind(this));
          this.labelObserver.observe(this.targetLabel, {
            childList: true,
            characterData: true,
            subtree: true
          });
        }

        if ('MutationObserver' in window) {
          this.stateObserver = new MutationObserver(this.syncState.bind(this));
          this.stateObserver.observe(this.target, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class']
          });

          if (this.targetCadence) {
            this.cadenceObserver = new MutationObserver(this.syncCadence.bind(this));
            this.cadenceObserver.observe(this.targetCadence, {
              childList: true,
              characterData: true,
              subtree: true
            });
          }
        }

        /* Reveal only once the real button has left through the TOP of the
           viewport. Appearing while the shopper is still above the buy box
           would cover content they have not reached yet. */
        if ('IntersectionObserver' in window) {
          this.visibility = new IntersectionObserver(
            function (entries) {
              entries.forEach(
                function (entry) {
                  var gone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                  this.hidden = !gone;
                  this.classList.toggle('is-visible', gone);
                }.bind(this)
              );
            }.bind(this),
            { threshold: 0 }
          );
          this.visibility.observe(this.target);
        }
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
        [this.labelObserver, this.stateObserver, this.cadenceObserver].forEach(function (o) {
          if (o) o.disconnect();
        });
        if (this.visibility) this.visibility.disconnect();
      }

      onClick(event) {
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true' || this.button.disabled) return;
        /* Native click on a type="submit" inside the product form, so the
           form's own submit handler runs exactly as if it were tapped. */
        this.target.click();
      }

      syncLabel() {
        if (!this.label) return;
        var text = this.targetLabel ? this.targetLabel.textContent : this.target.textContent;
        this.label.textContent = (text || '').trim();
      }

      syncCadence() {
        if (!this.cadence) return;
        this.cadence.textContent = this.targetCadence
          ? (this.targetCadence.textContent || '').trim()
          : '';
      }

      syncState() {
        var off =
          this.target.disabled || this.target.getAttribute('aria-disabled') === 'true';
        this.button.disabled = !!off;
        this.button.setAttribute('aria-disabled', off ? 'true' : 'false');
        this.button.classList.toggle('is-loading', this.target.classList.contains('is-loading'));
      }
    }
  );

  /* ------------------------------------------------------- social gallery */

  /*
   * UGC clips play only while they are on screen. Five autoplaying videos
   * decoding at once is a lot of work for a phone, and a wall of motion the
   * visitor never asked for; pausing the ones that scroll away costs nothing
   * and keeps the section calm.
   */
  /* Labels ride on the button so they stay translatable via the locale file. */
  function ugcLabel(button, state) {
    return button.getAttribute(state === 'on' ? 'data-label-on' : 'data-label-off') || '';
  }

  /* Only one clip is ever audible; a wall of overlapping soundtracks is noise. */
  function ugcSilenceAll(except) {
    var buttons = document.querySelectorAll('[data-ugc-sound]');
    Array.prototype.forEach.call(buttons, function (button) {
      var video = button.querySelector('video');
      if (!video || video === except) return;
      video.muted = true;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', ugcLabel(button, 'off'));
    });
  }

  function ugcSoundToggles(root) {
    var buttons = root.querySelectorAll('[data-ugc-sound]');

    Array.prototype.forEach.call(buttons, function (button) {
      if (button.dataset.ugcBound) return;
      button.dataset.ugcBound = 'true';

      button.addEventListener('click', function (event) {
        event.preventDefault();
        var video = button.querySelector('video');
        if (!video) return;

        var turningOn = video.muted;
        ugcSilenceAll(video);

        video.muted = !turningOn;
        button.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
        button.setAttribute('aria-label', ugcLabel(button, turningOn ? 'on' : 'off'));

        /* Unmuting counts as the user gesture, so a blocked clip can start here. */
        if (turningOn && video.paused) {
          var playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        }
      });

      var video = button.querySelector('video');
      if (video) {
        /* The right-click menu is the last route to a download link. */
        video.addEventListener('contextmenu', function (event) {
          event.preventDefault();
        });
      }
    });
  }

  function ugcVideos(scope) {
    var root = scope || document;
    ugcSoundToggles(root);

    var videos = root.querySelectorAll('video[data-ugc-autoplay]');
    if (!videos.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof window.IntersectionObserver === 'undefined') {
      /* Leave the poster frame showing. Tapping still turns sound on. */
      Array.prototype.forEach.call(videos, function (video) {
        video.removeAttribute('data-ugc-autoplay');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var playing = video.play();
            if (playing && typeof playing.catch === 'function') {
              /* Blocked (low power mode, data saver). The poster stays up. */
              playing.catch(function () {});
            }
          } else {
            /* Out of sight: stop the audio and reset that tile's button. */
            if (!video.paused) video.pause();
            if (!video.muted) {
              video.muted = true;
              var button = video.closest('[data-ugc-sound]');
              if (button) {
                button.setAttribute('aria-pressed', 'false');
                button.setAttribute('aria-label', ugcLabel(button, 'off'));
              }
            }
          }
        });
      },
      { threshold: 0.35 }
    );

    Array.prototype.forEach.call(videos, function (video) {
      observer.observe(video);
    });
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    loadRecommendations(document);
    scrollReveal(document);
    publishAnchorOffset();
    ugcVideos(document);
  }

  /* Re-arm after the theme editor swaps a section out. */
  document.addEventListener('shopify:section:load', function (event) {
    ugcVideos(event.target);
    scrollReveal(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
